import { Trans, useLingui } from '@lingui/react/macro'
import { maintenanceLogHooks, maintenanceTaskHooks } from '$src/lib/trpcHooks'
import { Button, Loader, Stack } from '@mantine/core'
import { AppPage } from '@markku/components'
import { keyBy } from '@markku/utils'
import { IconPlus } from '@tabler/icons-react'
import { useSearch } from '@tanstack/react-router'
import { useState } from 'react'
import { TaskDetailsModal } from '../../maintenance/components/TaskDetailsModal'
import { CostEntryModal } from './CostEntryModal'
import { CostsCategoryList } from './CostsCategoryList'
import { CostsHelpCard } from './CostsHelpCard'
import { CostsItemsTable } from './CostsItemsTable'
import { CostsMonthlyChart } from './CostsMonthlyChart'
import { CostsSummaryCard } from './CostsSummaryCard'
import {
  calculateCategoryData,
  calculateMonthlyData,
  calculateTotalBudget,
  calculateTotalCost,
  CostItem,
  getCostItemModalProps,
  getCostItems,
} from './utils'

export function CostsPage() {
  const { t } = useLingui()
  const search = useSearch({ from: '/_app/maintenance-costs' }) as {
    propertyId?: string
  }
  const propertyId = Number(search.propertyId)
  const year = new Date().getFullYear()

  const [showCreateModal, setShowCreateModal] = useState(false)
  const [editingItem, setEditingItem] = useState<CostItem | null>(null)

  const { data: logsData, isLoading: logsLoading } =
    maintenanceLogHooks.useList({
      pageSize: 1000,
      filters: {
        $and: [
          { propertyId: { eq: propertyId } },
          { completedDate: { gte: `${year}-01-01` } },
          { completedDate: { lte: `${year}-12-31` } },
        ],
      },
    })

  const { data: tasksData, isLoading: tasksLoading } =
    maintenanceTaskHooks.useList({
      pageSize: 1000,
      filters: {
        propertyId: { eq: propertyId },
      },
    })
  const tasks = tasksData?.data || []

  const tasksById = keyBy(tasks, (task) => task.id)

  const logs = (logsData?.data || []).filter((log) => {
    // Exclude system task log items / or logs for other properties
    return tasksById.has(log.maintenanceTaskId ?? 0)
  })

  const totalCost = calculateTotalCost(logs)
  const totalBudget = calculateTotalBudget(tasks)
  const monthlyData = calculateMonthlyData(logs, tasks)
  const categoryData = calculateCategoryData(logs)
  const costItems = getCostItems(logs, tasks, year)

  if (logsLoading || tasksLoading) {
    return (
      <AppPage
        title={t`Costs`}
        subtitle={t`Year ${year} cost tracking`}
        backTarget="/maintenance"
      >
        <Stack align="center" justify="center" h={300}>
          <Loader size="lg" />
        </Stack>
      </AppPage>
    )
  }

  const editModalProps = editingItem
    ? getCostItemModalProps(editingItem, tasks, logs)
    : { task: undefined, log: undefined }

  return (
    <AppPage
      title={t`Costs`}
      subtitle={t`Year ${year} cost tracking`}
      backTarget="/maintenance"
      rightSection={
        <Button
          leftSection={<IconPlus size={16} />}
          onClick={() => setShowCreateModal(true)}
        >
          <Trans>Add</Trans>
        </Button>
      }
    >
      <Stack gap="lg">
        <CostsSummaryCard
          year={year}
          totalBudget={totalBudget}
          totalCost={totalCost}
        />

        <CostsMonthlyChart data={monthlyData} />

        <CostsCategoryList categories={categoryData} />

        <CostsItemsTable items={costItems} onEdit={setEditingItem} />

        <CostsHelpCard />
      </Stack>

      {/* Create Modal - Simplified for costs */}
      <CostEntryModal
        propertyId={propertyId}
        opened={showCreateModal}
        onClose={() => setShowCreateModal(false)}
      />

      {/* Edit Modal - Full featured for editing */}
      {editingItem && (
        <TaskDetailsModal
          task={editModalProps.task}
          log={editModalProps.log}
          opened={!!editingItem}
          onClose={() => setEditingItem(null)}
        />
      )}
    </AppPage>
  )
}
